// @ts-check
import { useState, useCallback } from 'react';
import { licenseService } from '../../../apiService';
import { EVICTION_GRANT_COPY } from '../evictionGrantCopy';
import { DEVICE_BINDING_COPY } from '../deviceBindingCopy';
import { LABEL_REISSUE_COPY } from '../labelReissueCopy';

export const INITIAL_MODAL_STATE = {
  isOpen: false,
  title: '',
  message: '',
  variant: 'neutral',
  confirmText: undefined,
  confirmLabel: undefined,
  successToast: '',
  failureToast: '',
  action: null,
  submitting: false,
};

// Mirrors the backend's REGISTRATION_STATE_ATTRIBUTES (every field POST /register or its PoP
// enrolment sets). Admin unbind, unregister and regenerate-key all REMOVE them server side.
const CLEARED_REGISTRATION_STATE = Object.freeze({
  registered: false,
  registrationTimestamp: null,
  deviceId: null,
  registrationCodeUsed: false,
  popPublicKey: null,
  popPublicKeyEnrolledAt: null,
  popEnrolledDeviceId: null,
});

// Unregister and regenerate-key also REMOVE the activation label and its tracking in the same
// write (backend 3e279ba3); unbind keeps the label, so it uses CLEARED_REGISTRATION_STATE only.
const CLEARED_REGISTRATION_AND_LABEL_STATE = Object.freeze({
  ...CLEARED_REGISTRATION_STATE,
  activationSecretSetAt: null,
  labelPrintedAt: null,
  shippedAt: null,
});

// Bind 409 because the device ID is already bound to ANOTHER licence: the server message names
// that licence and is already clear. Every other bind 409 concerns this licence's own state.
const DEVICE_ON_OTHER_LICENCE = /already bound to license/i;

/**
 * @param {any} err
 * @param {string} serialNumber
 */
function bindErrorFor(err, serialNumber) {
  if (err?.status !== 409 || DEVICE_ON_OTHER_LICENCE.test(err?.message || '')) return err;
  return Object.assign(new Error(DEVICE_BINDING_COPY.bindConflict(serialNumber)), {
    status: err.status,
    body: err.body,
  });
}

/**
 * @typedef {Object} OptimisticDeps
 * @property {ReturnType<import('react-oidc-context').useAuth>} auth
 * @property {any[]} licenses
 * @property {(updater: (prev: any[]) => any[]) => void} setLicenses
 * @property {(err: string|null) => void} setError
 * @property {(loading: boolean) => void} setLoading
 * @property {() => void} refetch re-fetch the current page (used to revert failed mutations)
 * @property {(toast: { kind: string, message: string }) => void} addToast
 * @property {(updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void} setSelectedLicenses
 * @property {boolean} includeDeleted
 */

/**
 * Encapsulates every license mutation as an optimistic update guarded by a
 * confirmation modal, plus the bind/rebind modal lifecycle. Extracted from the
 * original LicenseTable with identical behavior; the only seam change is that
 * failed-mutation reverts call the injected `refetch` instead of re-deriving
 * fetch arguments inline.
 *
 * @param {OptimisticDeps} deps
 */
export function useOptimisticLicense(deps) {
  const {
    auth,
    licenses,
    setLicenses,
    setError,
    setLoading,
    refetch,
    addToast,
    setSelectedLicenses,
    includeDeleted,
  } = deps;

  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [bindModalState, setBindModalState] = useState({
    isOpen: false,
    serialNumber: '',
    currentDeviceId: /** @type {string|null} */ (null),
  });

  const closeConfirmModal = useCallback(() => setModalState(INITIAL_MODAL_STATE), []);

  // Replacement activation label to mint and show after unregister or regenerate-key. Closing it
  // re-reads the list so the row carries the server's new label state.
  const [labelReissue, setLabelReissue] = useState(
    /** @type {{ serialNumber: string, notice: string } | null} */ (null),
  );
  const closeLabelReissue = useCallback(() => {
    setLabelReissue(null);
    refetch();
  }, [refetch]);

  // Only a licence that HAD a label gets a replacement (read before the optimistic clear).
  const labelStateOf = useCallback(
    (serialNumber) => {
      const license = licenses.find((lic) => lic.serialNumber === serialNumber);
      return { hasLabel: Boolean(license?.activationSecretSetAt), isActive: license?.active !== false };
    },
    [licenses],
  );

  // Generic confirm-then-run wrapper. Each call site supplies copy, a variant,
  // the action, and optional optimistic/revert updaters + toast messages.
  const handleActionWithConfirmation = useCallback(
    ({
      title,
      message,
      variant = 'neutral',
      confirmText,
      confirmLabel,
      actionFn,
      optimisticUpdate,
      revertUpdate,
      successToast,
      failureToast,
    }) => {
      const runAction = async () => {
        setModalState((prev) => ({ ...prev, submitting: true }));
        if (optimisticUpdate) optimisticUpdate();
        setError(null);
        setLoading(true);
        try {
          await actionFn();
          if (successToast) addToast({ kind: 'success', message: successToast });
          setModalState(INITIAL_MODAL_STATE);
        } catch (err) {
          const msg = err && err.message ? err.message : 'Action failed';
          addToast({ kind: 'error', message: failureToast || msg });
          if (revertUpdate) revertUpdate();
          setModalState(INITIAL_MODAL_STATE);
        } finally {
          setLoading(false);
        }
      };

      setModalState({
        isOpen: true,
        title,
        message,
        variant,
        confirmText,
        confirmLabel,
        successToast,
        failureToast,
        action: runAction,
        submitting: false,
      });
    },
    [addToast, setError, setLoading],
  );

  const token = auth.user?.access_token;

  const handleEnable = useCallback(
    (serialNumber) =>
      handleActionWithConfirmation({
        title: `Enable license ${serialNumber}?`,
        message: 'The license will accept device validations again.',
        variant: 'neutral',
        confirmLabel: 'Enable',
        actionFn: () => licenseService.enableLicense(serialNumber, token),
        optimisticUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, active: true } : lic)),
          ),
        revertUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, active: false } : lic)),
          ),
        successToast: `Enabled ${serialNumber}`,
      }),
    [handleActionWithConfirmation, setLicenses, token],
  );

  const handleDisable = useCallback(
    (serialNumber) =>
      handleActionWithConfirmation({
        title: `Disable license ${serialNumber}?`,
        message: 'Devices using this license will fail validation until re-enabled.',
        variant: 'caution',
        confirmLabel: 'Disable',
        actionFn: () => licenseService.disableLicense(serialNumber, token),
        optimisticUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, active: false } : lic)),
          ),
        revertUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, active: true } : lic)),
          ),
        successToast: `Disabled ${serialNumber}`,
      }),
    [handleActionWithConfirmation, setLicenses, token],
  );

  const handleDelete = useCallback(
    (serialNumber) =>
      handleActionWithConfirmation({
        title: `Soft-delete license ${serialNumber}?`,
        message: 'Access will be revoked. The licence identity and retained archive recovery data are preserved; the licence can be restored.',
        variant: 'caution',
        confirmLabel: 'Soft delete',
        actionFn: () => licenseService.deleteLicense(serialNumber, token),
        optimisticUpdate: () =>
          setLicenses((prev) => prev.filter((lic) => lic.serialNumber !== serialNumber)),
        revertUpdate: refetch,
        successToast: `Soft-deleted ${serialNumber}`,
      }),
    [handleActionWithConfirmation, setLicenses, token, refetch],
  );

  const handleRestore = useCallback(
    (serialNumber) =>
      handleActionWithConfirmation({
        title: `Restore license ${serialNumber}?`,
        message: 'The license will be visible in default views again.',
        variant: 'neutral',
        confirmLabel: 'Restore',
        actionFn: () => licenseService.restoreLicense(serialNumber, token),
        optimisticUpdate: () =>
          setLicenses((prev) =>
            includeDeleted
              ? prev.map((lic) =>
                  lic.serialNumber === serialNumber
                    ? { ...lic, deleted: false, deletedAt: null, ttlExpiry: null }
                    : lic,
                )
              : prev.filter((lic) => lic.serialNumber !== serialNumber),
          ),
        revertUpdate: refetch,
        successToast: `Restored ${serialNumber}`,
      }),
    [handleActionWithConfirmation, setLicenses, token, refetch, includeDeleted],
  );

  const handlePermanentDelete = useCallback(
    () => addToast({ kind: 'error', message: 'Licence identity and retained archives cannot be permanently deleted. Use soft delete to revoke access.' }),
    [addToast],
  );

  // Regenerate-key also resets registration and revokes the activation label server side, so the
  // row is cleared like unregister and, if it had a label, the replacement opens straight after.
  const handleRegenerateKey = useCallback(
    (serialNumber) => {
      const { hasLabel, isActive } = labelStateOf(serialNumber);
      handleActionWithConfirmation({
        title: `Regenerate license key for ${serialNumber}?`,
        message: LABEL_REISSUE_COPY.regenerateBody(hasLabel),
        variant: 'caution',
        confirmLabel: 'Regenerate',
        actionFn: async () => {
          const result = await licenseService.regenerateLicenseKey(serialNumber, token);
          setLicenses((prev) =>
            prev.map((lic) =>
              lic.serialNumber === serialNumber
                ? { ...lic, ...CLEARED_REGISTRATION_AND_LABEL_STATE, licenseKey: result.licenseKey }
                : lic,
            ),
          );
          setSelectedLicenses((prev) => ({ ...prev, [serialNumber]: false }));
          if (hasLabel) {
            setLabelReissue({ serialNumber, notice: LABEL_REISSUE_COPY.regeneratedNotice(serialNumber, isActive) });
          }
          return result;
        },
        successToast: `Regenerated key for ${serialNumber}`,
      });
    },
    [handleActionWithConfirmation, labelStateOf, setLicenses, setSelectedLicenses, token],
  );

  const handleUnbind = useCallback(
    (serialNumber) =>
      handleActionWithConfirmation({
        title: DEVICE_BINDING_COPY.unbindTitle(serialNumber),
        message: DEVICE_BINDING_COPY.unbindBody,
        variant: 'caution',
        confirmLabel: DEVICE_BINDING_COPY.unbindConfirmLabel,
        actionFn: () => licenseService.unbindDevice(serialNumber, token),
        optimisticUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, ...CLEARED_REGISTRATION_STATE } : lic)),
          ),
        revertUpdate: refetch,
        successToast: DEVICE_BINDING_COPY.unboundToast(serialNumber),
      }),
    [handleActionWithConfirmation, setLicenses, token, refetch],
  );

  // Spec 5.3: the operator path for a wiped or replaced tablet. Unregister clears the licence's
  // registration AND its PoP enrolment server side, so the replacement installation can claim a
  // fresh registration code and bind its own key. Destructive from the field's point of view, so
  // it is confirmation-gated exactly like unbind. The same write revokes the activation label, so
  // if the licence had one, the replacement label opens straight after success.
  const handleUnregister = useCallback(
    (serialNumber) => {
      const { hasLabel, isActive } = labelStateOf(serialNumber);
      handleActionWithConfirmation({
        title: `Unregister the device from ${serialNumber}?`,
        message: LABEL_REISSUE_COPY.unregisterBody(hasLabel),
        variant: 'caution',
        confirmLabel: 'Unregister',
        actionFn: async () => {
          const result = await licenseService.unregisterDevice(serialNumber, token);
          if (hasLabel) {
            setLabelReissue({ serialNumber, notice: LABEL_REISSUE_COPY.unregisteredNotice(serialNumber, isActive) });
          }
          return result;
        },
        optimisticUpdate: () =>
          setLicenses((prev) =>
            prev.map((lic) =>
              lic.serialNumber === serialNumber
                ? { ...lic, ...CLEARED_REGISTRATION_AND_LABEL_STATE }
                : lic,
            ),
          ),
        revertUpdate: () => refetch(),
        successToast: `Unregistered device from ${serialNumber}`,
      });
    },
    [handleActionWithConfirmation, labelStateOf, setLicenses, refetch, token],
  );

  // WP4 task 2/3: per-licence waveform eviction grant toggle. Unlike enable/disable, this
  // is NOT optimistic - the row must show the CONFIRMED boolean from the response, never a
  // guess made before the request resolves. On failure the row is left exactly as it was
  // (handleActionWithConfirmation never applies an update here), and revertUpdate re-fetches
  // so a 409 (stale state) or any other failure shows the server's current truth, not a stale
  // client guess.
  const handleSetEvictionGrant = useCallback(
    (serialNumber, nextValue) =>
      handleActionWithConfirmation({
        title: nextValue
          ? EVICTION_GRANT_COPY.enableTitle(serialNumber)
          : EVICTION_GRANT_COPY.disableTitle(serialNumber),
        message: nextValue ? EVICTION_GRANT_COPY.enableBody : EVICTION_GRANT_COPY.disableBody,
        variant: nextValue ? 'caution' : 'neutral',
        confirmLabel: nextValue ? EVICTION_GRANT_COPY.enableConfirmLabel : EVICTION_GRANT_COPY.disableConfirmLabel,
        actionFn: async () => {
          const result = await licenseService.setEvictionGrant(serialNumber, nextValue, token);
          // Coerce strictly: only a literal true confirms the grant, matching the backend's
          // own strict projection. Reflect the response, never the request's intent.
          const confirmed = result?.evictionGrant === true;
          setLicenses((prev) =>
            prev.map((lic) =>
              lic.serialNumber === serialNumber ? { ...lic, evictionGrant: confirmed } : lic,
            ),
          );
          return result;
        },
        revertUpdate: refetch,
        successToast: nextValue
          ? EVICTION_GRANT_COPY.enabledToast(serialNumber)
          : EVICTION_GRANT_COPY.disabledToast(serialNumber),
      }),
    [handleActionWithConfirmation, setLicenses, token, refetch],
  );

  // Opens the BindDeviceModal; the modal owns the form, validation, and
  // signinSilent. Submit flows through onBindSubmit below.
  const handleBind = useCallback((serialNumber, currentDeviceId = null) => {
    setBindModalState({ isOpen: true, serialNumber, currentDeviceId });
  }, []);

  const closeBindModal = useCallback(
    () => setBindModalState({ isOpen: false, serialNumber: '', currentDeviceId: null }),
    [],
  );

  const onBindSubmit = useCallback(
    async (serialNumber, deviceId, accessToken) => {
      const original = licenses.find((l) => l.serialNumber === serialNumber)?.deviceId || null;
      setLicenses((prev) =>
        prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, deviceId } : lic)),
      );
      try {
        await licenseService.bindDevice(serialNumber, deviceId, accessToken);
        addToast({
          kind: 'success',
          message: original ? `Rebound ${serialNumber} to ${deviceId}` : `Bound ${serialNumber} to ${deviceId}`,
        });
      } catch (err) {
        // Revert and let the modal surface the error inline. A 409 means this row is stale
        // (registered or bound meanwhile), so re-fetch the server's truth as well.
        setLicenses((prev) =>
          prev.map((lic) => (lic.serialNumber === serialNumber ? { ...lic, deviceId: original } : lic)),
        );
        if (err?.status === 409) refetch();
        throw bindErrorFor(err, serialNumber);
      }
    },
    [licenses, setLicenses, addToast, refetch],
  );

  return {
    modalState,
    closeConfirmModal,
    bindModalState,
    closeBindModal,
    handleEnable,
    handleDisable,
    handleDelete,
    handleRestore,
    handlePermanentDelete,
    handleRegenerateKey,
    labelReissue,
    closeLabelReissue,
    handleBind,
    handleUnbind,
    handleUnregister,
    handleSetEvictionGrant,
    onBindSubmit,
  };
}
